// deno-lint-ignore-file
import { useState, useEffect, useMemo } from 'react'
import API from '../../services/api.js'
import { useRCMs, useRCMDetail } from '../../hooks/useRCM.js'
import { motion, useAnimation, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Plus, FileSpreadsheet, RefreshCw, Sparkles,
  ChevronDown, ChevronUp, AlertCircle, X, Pencil, CheckCircle2,
  User, ClipboardList, BookOpen, Save,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function toList(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') return value.split('\n').map(s => s.trim()).filter(Boolean)
  return []
}

function timeAgo(iso) {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  return `${Math.floor(days / 365)} years ago`
}

const LIST_GRID_COLS   = '110px 2fr 120px 140px 110px 100px 110px'
const MATRIX_GRID_COLS = '110px 2fr 120px 110px 100px 110px'

// ─────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    draft:     'bg-gray-500/10 text-gray-400 border border-gray-500/20',
    published: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    archived:  'bg-gray-500/10 text-gray-500 border border-gray-500/20',
    active:    'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  }
  return (
    <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium inline-flex items-center ${styles[status] || styles.draft}`}>
      {status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  )
}

function CoverageStatusBadge({ status }) {
  const styles = {
    mapped:         'bg-green-500/10 text-green-400 border border-green-500/20',
    partial:        'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
    gap:            'bg-red-500/10 text-red-400 border border-red-500/20',
    not_applicable: 'bg-gray-500/10 text-gray-500 border border-gray-500/20',
  }
  return (
    <span className={`justify-self-start px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium inline-flex items-center ${styles[status] || 'bg-gray-500/10 text-gray-400 border border-gray-500/20'}`}>
      {status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  )
}

// ─────────────────────────────────────────────
// Control Drawer
// ─────────────────────────────────────────────
function ControlDrawer({ rc, canEdit, onClose, onSave }) {
  const [tab, setTab] = useState('details')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saved, setSaved] = useState(false)

  const e = rc.enrichment || {}
  const resolved = rc.resolved || {}
  const hasOverride = !!rc.override

  const [form, setForm] = useState({
    implementation_override:  resolved.implementation  ?? '',
    process_owner_override:   resolved.process_owner   ?? '',
    testing_notes_override:   resolved.testing_notes   ?? '',
    review_comments:          resolved.review_comments  ?? '',
    further_comments:         resolved.further_comments ?? '',
  })

  function field(key) {
    return e => setForm(prev => ({ ...prev, [key]: e.target.value }))
  }

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      await onSave(rc.trusted_rcm_control_id, {
        implementation_override:  form.implementation_override  || null,
        process_owner_override:   form.process_owner_override   || null,
        testing_notes_override:   form.testing_notes_override   || null,
        review_comments:          form.review_comments          || null,
        further_comments:         form.further_comments         || null,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const frameworkTags = [
    e.nist_ai_rmf_refs && `NIST AI RMF: ${toList(e.nist_ai_rmf_refs).join(', ')}`,
    e.eu_ai_act_refs   && `EU AI Act: ${toList(e.eu_ai_act_refs).join(', ')}`,
    e.iso_42001_ref && e.iso_42001_ref !== 'X' && `ISO 42001: ${e.iso_42001_ref}`,
    e.article_mapping  && `Articles: ${e.article_mapping.replace(/\n/g, ', ')}`,
  ].filter(Boolean)

  const lifecyclePhases = toList(e.ai_lifecycle_phases)

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="drawer-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.85)' }}
        onClick={onClose}
      >
        <motion.div
          key="drawer-modal"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.15 }}
          onClick={e => e.stopPropagation()}
          className="w-full flex flex-col"
          style={{
            zIndex: 10000,
            maxWidth: '540px',
            maxHeight: '82vh',
            backgroundColor: '#111214',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px',
          }}
        >
          <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-2.5 border-b border-white/10 shrink-0">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-blue-400 text-xs">{rc.controls?.control_id ?? '—'}</span>
                {rc.controls?.category && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-400">
                    {rc.controls.category}
                  </span>
                )}
                {hasOverride && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center gap-1">
                    <Pencil className="w-2 h-2" /> Customised
                  </span>
                )}
              </div>
              <h2 className="text-white text-xs font-semibold mt-0.5 leading-snug">
                {rc.controls?.title ?? '—'}
              </h2>
              {resolved.last_reviewed_by && (
                <p>Last reviewed by {resolved.last_reviewed_by} · {timeAgo(resolved.last_reviewed_at)}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-2 px-4 py-1.5 border-b border-white/5 bg-white/[0.02] shrink-0">
            <CoverageStatusBadge status={rc.coverage_status} />
            <span className="text-[10px] text-gray-500">
              {rc.evidence_count > 0
                ? `${rc.evidence_count} evidence item${rc.evidence_count !== 1 ? 's' : ''}`
                : 'No evidence linked'}
            </span>
            {resolved.process_owner && (
              <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-400">
                <User className="w-2.5 h-2.5" />
                {resolved.process_owner}
              </span>
            )}
          </div>

          <div className="flex border-b border-white/10 shrink-0">
            {[
              { id: 'details', label: 'Details', icon: BookOpen },
              { id: 'implementation', label: 'Implementation', icon: ClipboardList },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`flex items-center gap-1 px-3 py-2 text-[11px] font-medium border-b-2 transition-colors duration-150 ${
                  tab === id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {tab === 'details' && (
              <div className="px-4 py-3 space-y-3">
                {e.control_description && (
                  <DenseSection title="Description">
                    <p className="text-gray-300 text-[11px] leading-relaxed">{e.control_description}</p>
                  </DenseSection>
                )}

                {e.l1_risk_title && (
                  <DenseSection title="Risk context">
                    <div className="space-y-1.5">
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide">L1 risk</p>
                        <p className="text-[11px] text-gray-300 font-medium">{e.l1_risk_title}</p>
                        {e.l1_risk_description && (
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{e.l1_risk_description}</p>
                        )}
                      </div>
                      {e.l2_risk_title && (
                        <div className="pl-2 border-l border-white/10">
                          <p className="text-[9px] text-gray-500 uppercase tracking-wide">
                            L2 risk {e.risk_id && <span className="font-mono normal-case">{e.risk_id}</span>}
                          </p>
                          <p className="text-[11px] text-gray-300 font-medium">{e.l2_risk_title}</p>
                          {e.l2_risk_description && (
                            <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{e.l2_risk_description}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </DenseSection>
                )}

                {toList(e.control_attributes).length > 0 && (
                  <DenseSection title="Control attributes">
                    <ul className="space-y-0.5">
                      {toList(e.control_attributes).map((attr, i) => (
                        <li key={i} className="flex gap-1.5 text-[10px] text-gray-400">
                          <span className="text-gray-600 shrink-0">{i + 1}.</span>
                          <span className="leading-relaxed">{attr}</span>
                        </li>
                      ))}
                    </ul>
                  </DenseSection>
                )}

                {toList(e.evidence_required).length > 0 && (
                  <DenseSection title="Evidence required">
                    <ul className="space-y-0.5">
                      {toList(e.evidence_required).map((item, i) => (
                        <li key={i} className="flex gap-1.5 text-[10px] text-gray-400">
                          <span className="text-blue-500 shrink-0">·</span>
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </DenseSection>
                )}

                {frameworkTags.length > 0 && (
                  <DenseSection title="Framework mappings">
                    <div className="flex flex-wrap gap-1">
                      {frameworkTags.map((tag, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[9px] text-gray-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </DenseSection>
                )}

                {lifecyclePhases.length > 0 && (
                  <DenseSection title="AI lifecycle phases">
                    <div className="flex flex-wrap gap-1">
                      {lifecyclePhases.map((phase, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-[9px] text-blue-400">
                          {phase}
                        </span>
                      ))}
                    </div>
                  </DenseSection>
                )}

                {(e.domain || e.control_level) && (
                  <DenseSection title="Classification">
                    <div className="grid grid-cols-2 gap-2">
                      {e.domain && (
                        <div>
                          <p className="text-[9px] text-gray-500 uppercase tracking-wide">Domain</p>
                          <p className="text-[10px] text-gray-300">{e.domain}</p>
                        </div>
                      )}
                      {e.control_level && (
                        <div>
                          <p className="text-[9px] text-gray-500 uppercase tracking-wide">Control level</p>
                          <p className="text-[10px] text-gray-300">{e.control_level}</p>
                        </div>
                      )}
                    </div>
                  </DenseSection>
                )}
              </div>
            )}

            {tab === 'implementation' && (
              <div className="px-4 py-3 space-y-3">
                {!canEdit && (
                  <div className="flex items-center gap-1.5 px-2.5 py-2 rounded bg-yellow-500/5 border border-yellow-500/20">
                    <AlertCircle className="w-3 h-3 text-yellow-400 shrink-0" />
                    <p className="text-[10px] text-yellow-400">View only — admin or analyst role required to edit</p>
                  </div>
                )}

                <DenseField
                  label="Implementation"
                  placeholder={e.control_implementation ?? 'No implementation guidance available'}
                  value={form.implementation_override}
                  onChange={field('implementation_override')}
                  readOnly={!canEdit}
                  rows={4}
                  isOverridden={!!rc.override?.implementation_override}
                  catalogValue={e.control_implementation}
                />

                <DenseField
                  label="Process owner"
                  placeholder={e.probable_process_owner ?? 'e.g. CISO, CTO, CPO'}
                  value={form.process_owner_override}
                  onChange={field('process_owner_override')}
                  readOnly={!canEdit}
                  rows={1}
                  isOverridden={!!rc.override?.process_owner_override}
                  catalogValue={e.probable_process_owner}
                />

                <DenseField
                  label="Testing notes"
                  placeholder={e.testing_attributes ?? 'No testing guidance available'}
                  value={form.testing_notes_override}
                  onChange={field('testing_notes_override')}
                  readOnly={!canEdit}
                  rows={3}
                  isOverridden={!!rc.override?.testing_notes_override}
                  catalogValue={e.testing_attributes}
                />

                <DenseField
                  label="Review comments"
                  placeholder="Add review notes for this cycle..."
                  value={form.review_comments}
                  onChange={field('review_comments')}
                  readOnly={!canEdit}
                  rows={2}
                  isOverridden={!!rc.override?.review_comments}
                />

                <DenseField
                  label="Further comments"
                  placeholder="Additional comments..."
                  value={form.further_comments}
                  onChange={field('further_comments')}
                  readOnly={!canEdit}
                  rows={2}
                  isOverridden={!!rc.override?.further_comments}
                />

                {saveError && (
                  <div className="flex items-start gap-1.5 px-2.5 py-2 rounded bg-red-500/5 border border-red-500/20">
                    <AlertCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-red-400">{saveError}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {tab === 'implementation' && canEdit && (
            <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between gap-3 shrink-0" style={{ backgroundColor: '#111214' }}>
              {saved ? (
                <span className="flex items-center gap-1 text-[11px] text-green-400">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              ) : (
                <span className="text-[10px] text-gray-600">Org-specific — doesn't affect the catalog</span>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-[11px] rounded bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="w-3 h-3" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

function DenseSection({ title, children }) {
  return (
    <div>
      <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{title}</p>
      {children}
    </div>
  )
}

function DenseField({ label, placeholder, value, onChange, readOnly, rows, isOverridden, catalogValue }) {
  const [showCatalog, setShowCatalog] = useState(false)
  const isTextarea = rows > 1

  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <p className="text-[10px] font-medium text-gray-300">{label}</p>
        <div className="flex items-center gap-1.5">
          {isOverridden && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center gap-0.5">
              <Pencil className="w-2 h-2" /> Edited
            </span>
          )}
          {catalogValue && !readOnly && (
            <button
              type="button"
              onClick={() => setShowCatalog(v => !v)}
              className="text-[9px] text-gray-500 hover:text-gray-300 underline"
            >
              {showCatalog ? 'Hide catalog' : 'View catalog'}
            </button>
          )}
        </div>
      </div>

      {showCatalog && catalogValue && (
        <div className="mb-1.5 p-2 rounded bg-white/[0.03] border border-white/10 text-[10px] text-gray-500 leading-relaxed whitespace-pre-wrap max-h-28 overflow-y-auto">
          {catalogValue}
        </div>
      )}

      {isTextarea ? (
        <textarea
          rows={rows}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={readOnly ? (value || placeholder) : placeholder}
          className={`w-full px-2 py-1.5 rounded text-[11px] leading-relaxed resize-none border transition-colors
            ${readOnly
              ? 'bg-white/[0.02] border-white/5 text-gray-400 cursor-default'
              : 'bg-white/5 border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'}
          `}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={readOnly ? (value || placeholder) : placeholder}
          className={`w-full px-2 py-1.5 rounded text-[11px] border transition-colors
            ${readOnly
              ? 'bg-white/[0.02] border-white/5 text-gray-400 cursor-default'
              : 'bg-white/5 border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'}
          `}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// Risk Matrix
// ─────────────────────────────────────────────
const UNSPECIFIED_PILLAR = 'Other'
const UNSPECIFIED_L1     = 'Ungrouped Risk'
const UNSPECIFIED_L2     = 'General'

function buildRiskMatrix(rcmControls) {
  const pillars = new Map()

  for (const rc of rcmControls) {
    const e = rc.enrichment || {}
    const pillar  = e.pillar          || rc.controls?.category || UNSPECIFIED_PILLAR
    const l1Title = e.l1_risk_title   || UNSPECIFIED_L1
    const l1Desc  = e.l1_risk_description || null
    const l2Title = e.l2_risk_title   || UNSPECIFIED_L2
    const l2Desc  = e.l2_risk_description || null
    const riskId  = e.risk_id         || null

    if (!pillars.has(pillar)) pillars.set(pillar, new Map())
    const l1Map = pillars.get(pillar)
    if (!l1Map.has(l1Title)) l1Map.set(l1Title, { description: l1Desc, l2s: new Map() })
    const l1Entry = l1Map.get(l1Title)
    if (!l1Entry.l2s.has(l2Title)) l1Entry.l2s.set(l2Title, { description: l2Desc, riskId, controls: [] })
    l1Entry.l2s.get(l2Title).controls.push(rc)
  }

  return [...pillars.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([pillar, l1Map]) => ({
      pillar,
      l1Risks: [...l1Map.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([l1Title, l1Entry]) => ({
          title: l1Title,
          description: l1Entry.description,
          l2Risks: [...l1Entry.l2s.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([l2Title, l2Entry]) => ({
              title: l2Title,
              description: l2Entry.description,
              riskId: l2Entry.riskId,
              controls: l2Entry.controls,
            })),
        })),
    }))
}

function MatrixControlRow({ rc, canEdit, isDraft, removeControl, onOpenDrawer }) {
  const hasOverride = !!rc.override

  return (
    <div className="hover:bg-white/[0.02] transition">
      <div
        className="grid gap-2 px-4 py-2.5 items-center"
        style={{ gridTemplateColumns: MATRIX_GRID_COLS }}
      >
        <span className="font-mono text-blue-400 text-xs truncate">{rc.controls?.control_id ?? '—'}</span>

        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-white text-xs truncate">{rc.controls?.title ?? '—'}</span>
          {hasOverride && (
            <span title="Customised by your org">
              <Pencil className="w-2.5 h-2.5 text-blue-400 shrink-0" />
            </span>
          )}
        </span>

        <span className="flex items-center gap-1 text-[11px] text-gray-500 truncate">
          {rc.resolved?.process_owner
            ? <><User className="w-3 h-3 shrink-0" />{rc.resolved.process_owner}</>
            : <span className="text-gray-700">—</span>}
        </span>

        <CoverageStatusBadge status={rc.coverage_status} />

        <span className="text-gray-400 text-xs">
          {rc.evidence_count > 0
            ? `${rc.evidence_count} item${rc.evidence_count !== 1 ? 's' : ''}`
            : <span className="text-gray-600">None</span>}
        </span>

        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onOpenDrawer(rc)}
            className="px-2 py-1 text-[10px] rounded bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            Open
          </button>
          {canEdit && isDraft && (
            <button
              type="button"
              onClick={() => removeControl(rc.id)}
              className="px-2 py-1 text-[10px] bg-white/5 border border-white/10 rounded-md text-gray-500 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-colors"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function RiskMatrixView({ rcmControls, canEdit, isDraft, removeControl, onOpenDrawer }) {
  const [collapsedPillars, setCollapsedPillars] = useState({})
  const [collapsedL1, setCollapsedL1] = useState({})

  const matrix = useMemo(() => buildRiskMatrix(rcmControls), [rcmControls])

  if (matrix.length === 0) {
    return (
      <div className="text-center py-14 text-gray-500 px-4">
        <p className="text-base text-gray-300">No controls to display</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-white/5">
      {matrix.map(({ pillar, l1Risks }) => {
        const pillarCount = l1Risks.reduce((s, l1) => s + l1.l2Risks.reduce((s2, l2) => s2 + l2.controls.length, 0), 0)
        const collapsed = !!collapsedPillars[pillar]

        return (
          <div key={pillar}>
            <button
              type="button"
              onClick={() => setCollapsedPillars(p => ({ ...p, [pillar]: !p[pillar] }))}
              className="w-full flex items-center justify-between px-4 sm:px-6 py-3 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-left"
            >
              <span className="text-sm font-semibold text-white">{pillar}</span>
              <span className="flex items-center gap-2 text-xs text-gray-500">
                {pillarCount} control{pillarCount !== 1 ? 's' : ''}
                {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </span>
            </button>

            {!collapsed && l1Risks.map(l1 => {
              const l1Key = `${pillar}::${l1.title}`
              const l1Count = l1.l2Risks.reduce((s, l2) => s + l2.controls.length, 0)
              const l1Collapsed = !!collapsedL1[l1Key]

              return (
                <div key={l1Key} className="border-t border-white/5">
                  <button
                    type="button"
                    onClick={() => setCollapsedL1(p => ({ ...p, [l1Key]: !p[l1Key] }))}
                    className="w-full flex items-start justify-between gap-4 px-4 sm:px-6 py-3 pl-8 sm:pl-12 hover:bg-white/[0.02] transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-blue-300">{l1.title}</p>
                      {l1.description && (
                        <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{l1.description}</p>
                      )}
                    </div>
                    <span className="flex items-center gap-1.5 text-[11px] text-gray-500 shrink-0 mt-0.5">
                      {l1Count} control{l1Count !== 1 ? 's' : ''}
                      {l1Collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                    </span>
                  </button>

                  {!l1Collapsed && l1.l2Risks.map(l2 => (
                    <div key={l2.title} className="pl-10 sm:pl-16 pr-4 sm:pr-6 pb-3">
                      <div className="flex items-center gap-2 py-2">
                        <p className="text-[11px] font-medium text-gray-300">{l2.title}</p>
                        {l2.riskId && (
                          <span className="text-[10px] font-mono text-gray-600">{l2.riskId}</span>
                        )}
                        {l2.description && (
                          <p className="text-[10px] text-gray-600 hidden sm:block truncate">{l2.description}</p>
                        )}
                      </div>

                      <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden divide-y divide-white/5">
                        <div
                          className="grid gap-2 px-4 py-1.5 border-b border-white/5"
                          style={{ gridTemplateColumns: MATRIX_GRID_COLS }}
                        >
                          {['ID', 'Control', 'Owner', 'Coverage', 'Evidence', ''].map((h, i) => (
                            <span key={i} className="text-[10px] uppercase tracking-wide text-gray-600 font-medium">{h}</span>
                          ))}
                        </div>
                        {l2.controls.map(rc => (
                          <MatrixControlRow
                            key={rc.id}
                            rc={rc}
                            canEdit={canEdit}
                            isDraft={isDraft}
                            removeControl={removeControl}
                            onOpenDrawer={onOpenDrawer}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────
// Pager
// ─────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function Pager({ page, totalPages, totalItems, rangeStart, rangeEnd, pageSize, onPageChange, onPageSizeChange }) {
  if (totalItems === 0) return null

  return (
    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-white/5">
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="hidden sm:inline">Rows per page:</span>
        <div className="relative inline-block">
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="appearance-none bg-neutral-900 border border-white/10 rounded px-2 py-1 pr-8 text-gray-300 text-[11px] focus:outline-none focus:border-blue-500 hover:border-white/20 transition-colors"
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n} className="bg-neutral-900 text-gray-300">
                {n}
              </option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[11px] text-gray-500">
          {rangeStart}–{rangeEnd} of {totalItems}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            aria-label="First page"
          >
            <ChevronsLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            aria-label="Last page"
          >
            <ChevronsRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────
function Header({ onBack, onNew }) {
  return (
    <div className="mb-6 sm:mb-10">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 min-w-0">
          <motion.button
            onClick={onBack}
            whileHover={{ scale: 1.08, x: -2 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 260, damping: 16 }}
            className="w-10 h-10 inline-flex items-center justify-center text-gray-400 hover:text-white shrink-0 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight truncate">
            RCM Builder
            <div className="mt-2 h-[2px] w-20 sm:w-40 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
          </h1>
        </div>
        <button
          type="button"
          onClick={onNew}
          className="inline-flex items-center text-white gap-2 px-4 py-2 text-sm bg-white/5 border border-gray-700 rounded-lg hover:bg-white/10 transition shrink-0"
        >
          <Plus className="w-4 h-4" />
          New RCM
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Auto RCM Card
// ─────────────────────────────────────────────
function AutoRCMCard({ autoRCM, pendingQueueCount, failedQueueCount, syncing, syncError, onSync, onRebuild, canRebuild, onOpenAutoRCM }) {
  return (
    <div className="px-4 sm:px-6 py-5 border-b border-white/10 bg-gradient-to-r from-blue-500/5 to-transparent">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white text-sm font-medium">
                {autoRCM ? autoRCM.name : 'Auto-Generated RCM'}
              </p>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Auto-Sync
              </span>
              {pendingQueueCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                  {pendingQueueCount} pending
                </span>
              )}
              {failedQueueCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                  {failedQueueCount} failed
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {autoRCM
                ? `Continuously synced from accepted mappings · ${autoRCM.version ? `v${autoRCM.version}` : ''}`
                : 'Accept mappings in Policies to populate this RCM'}
            </p>
            {syncError && (
              <div className="flex items-start gap-1.5 mt-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-red-400 leading-snug break-all">{syncError}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          {canRebuild && (
            <button
              type="button"
              onClick={() => onRebuild().catch(() => {})}
              disabled={syncing}
              title="Rebuild this asset's RCM from accepted policy mappings"
              className="px-3 py-2 text-xs rounded-md bg-white/5 border border-white/10 text-gray-300 hover:text-white disabled:opacity-40"
            >
              Rebuild
            </button>
          )}
          <motion.button
            onClick={onSync}
            disabled={syncing}
            whileHover={!syncing ? { scale: 1.04 } : {}}
            whileTap={!syncing ? { scale: 0.96 } : {}}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200
              ${syncError
                ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20'
                : 'bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:bg-white/10'}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : syncError ? 'Retry' : 'Sync Now'}
          </motion.button>
          {/* Always render Open — no longer gated on autoRCM being loaded yet */}
          <button
            type="button"
            onClick={onOpenAutoRCM}
            className="px-3 py-2 text-xs bg-blue-500/10 border border-blue-500/20 rounded-md text-blue-400 hover:bg-blue-500/20 transition-all duration-200"
          >
            Open
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// RCM List View
// ─────────────────────────────────────────────
function RCMList({ onSelect, onNew, rcms, autoRCM, pendingQueueCount, failedQueueCount, loading, syncing, syncError, updateRCM, syncRCM, rebuildRCM, canRebuild, pageAssetId }) {

  async function handleDuplicate(rcm) {
    try {
      const newRcm = await createDuplicateRCM(rcm)
      onSelect(newRcm.id)
    } catch (err) {
      alert(`Duplicate failed: ${err?.response?.data?.detail || err.message}`)
    }
  }

  async function handleOpenAutoRCM() {
    if (autoRCM) {
      await API.post('/rcm/sync/', { asset_id: pageAssetId }).catch(() => {})   // ADDED — drain queue before navigating in
      onSelect(autoRCM.id)
      return
    }
    try {
      const { data } = await API.post('/rcm/seed/', { asset_id: pageAssetId })
      const rcmId = data?.data?.rcm_id
      if (rcmId) {
        await API.post('/rcm/sync/', { asset_id: pageAssetId }).catch(() => {})   // ADDED
        onSelect(rcmId)
      } else {
        alert('Could not open Auto-Generated RCM. Please try again.')
      }
    } catch (err) {
      alert(`Could not open Auto-Generated RCM: ${err?.response?.data?.detail || err.message}`)
    }
  }

  async function createDuplicateRCM(rcm) {
    const { data: controls } = await API.get(`/rcm/${rcm.id}/controls/`, { params: { asset_id: pageAssetId } })
    const { data: newRcm } = await API.post('/rcm/', {
      asset_id: pageAssetId,
      name: `${rcm.name} (Copy)`,
      description: rcm.description,
      version: '1.0',
    })
    for (const c of controls || []) {
      if (!c.control_id) continue
      try {
        await API.post(`/rcm/${newRcm.id}/controls/`, {
          control_id: c.control_id,
          notes: c.notes || null,
          asset_id: pageAssetId,
        })
      } catch {
        // best-effort — a single failed control shouldn't abort the whole duplicate
      }
    }
    return newRcm
  }

  if (loading) return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    </div>
  )

  return (
    <div className="divide-y divide-white/5">
      <AutoRCMCard
        autoRCM={autoRCM}
        pendingQueueCount={pendingQueueCount}
        failedQueueCount={failedQueueCount}
        syncing={syncing}
        syncError={syncError}
        onSync={syncRCM}
        onRebuild={rebuildRCM}
        canRebuild={canRebuild}
        onOpenAutoRCM={handleOpenAutoRCM}
      />
      {!rcms?.length ? (
        <div className="text-center py-10 text-gray-500 px-4">
          <p className="text-sm text-gray-400">No manual RCMs yet</p>
          <p className="text-xs mt-1">Create one with the New RCM button above</p>
        </div>
      ) : (
        rcms.map(rcm => (
          <div
            key={rcm.id}
            onClick={() => onSelect(rcm.id)}
            className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 py-4 hover:bg-white/5 transition cursor-pointer"
          >
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm sm:text-base font-medium truncate">{rcm.name}</p>
              {rcm.description && <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">{rcm.description}</p>}
              <div className="flex items-center gap-3 mt-2">
                <p className="text-[11px] text-gray-500">Created {new Date(rcm.created_at).toLocaleDateString()}</p>
                <span className="text-[11px] font-mono text-gray-600">v{rcm.version}</span>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto" onClick={e => e.stopPropagation()}>
              <StatusBadge status={rcm.status} />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => onSelect(rcm.id)} className="px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200">Open</button>
                <button type="button" onClick={() => handleDuplicate(rcm)} className="px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200">Duplicate</button>
                {rcm.status !== 'archived' && (
                  <button type="button" onClick={() => updateRCM(rcm.id, { status: 'archived' })} className="px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-gray-400 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-all duration-200">Archive</button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// RCM Detail View
// ─────────────────────────────────────────────
function RCMDetail({ rcmId, pageAssetId, updateRCM, syncRCM, syncing, syncError, pendingQueueCount }) {
  const { rcm, rcmControls, loading, addControl, removeControl, saveOverride, currentUser } = useRCMDetail(rcmId, pageAssetId)

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [addError, setAddError] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [showClearControlsConfirm, setShowClearControlsConfirm] = useState(false)
  const [clearingControls, setClearingControls] = useState(false)
  const [viewMode, setViewMode] = useState('list')
  const [drawerRc, setDrawerRc] = useState(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const isAutoRCM = rcm?.source === 'auto'

  const canEdit = !isAutoRCM && (currentUser?.role === 'super_admin' || currentUser?.role === 'client_admin')
  const canEditImplementation = currentUser?.role === 'super_admin' || currentUser?.role === 'client_admin'

  const addedControlIds = useMemo(
    () => new Set(rcmControls.map(rc => rc.control_id)),
    [rcmControls]
  )

  const totalItems = rcmControls.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const safePage = Math.min(page, totalPages)
  const startIdx = (safePage - 1) * pageSize
  const endIdx = Math.min(startIdx + pageSize, totalItems)
  const pagedControls = useMemo(
    () => rcmControls.slice(startIdx, endIdx),
    [rcmControls, startIdx, endIdx]
  )

  useEffect(() => {
    setPage(1)
  }, [rcmId, viewMode, pageSize])

  useEffect(() => {
    if (!drawerRc) return
    const updated = rcmControls.find(r => r.id === drawerRc.id)
    if (updated) {
      setDrawerRc(updated)
    }
  }, [drawerRc, rcmControls])

  async function handleSearch(query) {
    setSearchQuery(query)
    if (!query.trim()) { setSearchResults([]); return }
    setSearching(true)
    try {
      const { data } = await API.get('/controls/', { params: { search: query, limit: 20, asset_id: pageAssetId } })
      setSearchResults(data || [])
    } catch (err) {
      console.error('Control search failed:', err?.response?.data?.detail || err.message)
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  async function handleAddControl(control) {
    setAddError(null)
    try {
      await addControl(control.id, null)
      setSearchResults(prev => prev.filter(c => c.id !== control.id))
    } catch (err) {
      setAddError(err.message)
    }
  }

  async function handleExport() {
    setExporting(true)
    try {
      const response = await API.get(`/rcm/${rcmId}/export/`, {
        responseType: 'blob',
        params: { asset_id: pageAssetId },
      })
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${rcm?.name || 'RCM'}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Export failed: ${err?.response?.data?.detail || err.message}`)
    } finally {
      setExporting(false)
    }
  }

  async function handleRemoveAllControls() {
    try {
      setClearingControls(true)
      await Promise.all(rcmControls.map(rc => removeControl(rc.id)))
      setShowClearControlsConfirm(false)
    } catch (err) {
      alert(`Failed to remove controls: ${err.message}`)
    } finally {
      setClearingControls(false)
    }
  }

  if (loading) return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">

      {drawerRc && (
        <ControlDrawer
          rc={drawerRc}
          canEdit={canEditImplementation}
          onClose={() => setDrawerRc(null)}
          onSave={saveOverride}
        />
      )}

      <div className={`flex flex-wrap items-center justify-between gap-4 px-4 sm:px-6 py-4 border rounded-xl
        ${isAutoRCM ? 'bg-gradient-to-r from-blue-500/5 to-transparent border-blue-500/20' : 'bg-white/5 border-white/10'}`}>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAutoRCM && <Sparkles className="w-4 h-4 text-blue-400 shrink-0" />}
            <p className="text-white font-medium text-sm sm:text-base">{rcm?.name}</p>
            {isAutoRCM && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Auto-Sync
              </span>
            )}
          </div>
          {rcm?.description && <p className="text-xs text-gray-400 mt-0.5">{rcm.description}</p>}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <StatusBadge status={rcm?.status} />
            <span className="text-[11px] font-mono text-gray-600">v{rcm?.version}</span>
            {isAutoRCM && pendingQueueCount > 0 && (
              <span className="text-[11px] text-yellow-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                {pendingQueueCount} controls pending enrichment
              </span>
            )}
          </div>
          {isAutoRCM && syncError && (
            <div className="flex items-start gap-1.5 mt-2">
              <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-400 leading-snug break-all">{syncError}</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isAutoRCM && (
            <motion.button
              onClick={syncRCM}
              disabled={syncing}
              whileHover={!syncing ? { scale: 1.04 } : {}}
              whileTap={!syncing ? { scale: 0.96 } : {}}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200
                ${syncError
                  ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20'
                  : 'bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:bg-white/10'}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : syncError ? 'Retry' : 'Sync Now'}
            </motion.button>
          )}
          {!isAutoRCM && rcm?.status === 'draft' && canEdit && (
            <motion.button
              onClick={() => updateRCM(rcmId, { status: 'published' })}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              className="px-3 py-2 text-xs bg-blue-500/10 border border-blue-500/20 rounded-md text-blue-400 hover:bg-blue-500/20 transition-colors duration-200"
            >
              Publish
            </motion.button>
          )}
          {!isAutoRCM && rcm?.status === 'published' && canEdit && (
            <motion.button
              onClick={() => setShowArchiveConfirm(true)}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              className="px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-gray-400 hover:text-red-400 hover:border-red-500/40 hover:bg-red-500/10 transition-colors duration-200"
            >
              Archive
            </motion.button>
          )}
          <motion.button
            onClick={handleExport}
            disabled={exporting}
            whileHover={!exporting ? { scale: 1.04 } : {}}
            whileTap={!exporting ? { scale: 0.96 } : {}}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-md text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FileSpreadsheet size={14} />
            {exporting ? 'Exporting...' : 'Export XLSX'}
          </motion.button>
        </div>
      </div>

      {showArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-neutral-900 border border-white/10 rounded-xl p-6 shadow-xl">
            <h3 className="text-white text-sm font-semibold mb-2">Confirm Archive</h3>
            <p className="text-gray-400 text-xs mb-6">Are you sure you want to archive this RCM?</p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowArchiveConfirm(false)} className="px-4 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors duration-200">Cancel</button>
              <button type="button" onClick={() => { updateRCM(rcmId, { status: 'archived' }); setShowArchiveConfirm(false) }} className="px-4 py-2 text-xs rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors duration-200">Archive</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canEdit && rcm?.status === 'draft' && !isAutoRCM && (
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden p-4 sm:p-6"
            >
              <h3 className="text-sm font-semibold text-white mb-4">Add Controls</h3>
              <input
                type="text" value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by ID or title..."
                className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 text-white placeholder-gray-500 border border-white/10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200 mb-4"
              />
              {addError && <p className="text-xs text-red-400 mb-3">{addError}</p>}
              {searching ? (
                <p className="text-xs text-gray-500">Searching...</p>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map(control => {
                    const alreadyAdded = addedControlIds.has(control.id)
                    return (
                      <div key={control.id} className="bg-white/5 border border-white/10 rounded-lg p-3 flex justify-between items-start hover:bg-white/10 transition">
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="text-xs font-mono text-blue-400">{control.control_id}</p>
                          <p className="text-xs text-gray-300 mt-0.5 truncate">{control.title}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5">{control.frameworks?.name}</p>
                        </div>
                        <button type="button" onClick={() => handleAddControl(control)} disabled={alreadyAdded}
                          className="text-xs text-green-400 hover:text-green-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors shrink-0">
                          {alreadyAdded ? 'Added' : '+ Add'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : searchQuery ? (
                <p className="text-xs text-gray-500">No controls found</p>
              ) : (
                <p className="text-xs text-gray-500">Type to search controls by ID or title</p>
              )}
            </motion.div>
          </div>
        )}

        <div className={canEdit && rcm?.status === 'draft' && !isAutoRCM ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <motion.div
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
            className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/5">
              <h3 className="text-sm font-semibold text-white">
                Controls
                <span className="text-gray-500 font-normal ml-2">({rcmControls.length})</span>
              </h3>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={`px-2.5 py-1 text-[11px] rounded transition-colors duration-200 ${viewMode === 'list' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-400 hover:text-white'}`}
                  >
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('matrix')}
                    className={`px-2.5 py-1 text-[11px] rounded transition-colors duration-200 ${viewMode === 'matrix' ? 'bg-blue-500/20 text-blue-300' : 'text-gray-400 hover:text-white'}`}
                  >
                    Matrix
                  </button>
                </div>
                {canEdit && rcm?.status === 'draft' && rcmControls.length > 0 && (
                  <button type="button" onClick={() => setShowClearControlsConfirm(true)}
                    className="px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-md text-gray-400 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-colors duration-200">
                    Remove All
                  </button>
                )}
              </div>
            </div>

            {showClearControlsConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
                <div className="w-full max-w-sm bg-neutral-900 border border-white/10 rounded-xl p-6 shadow-xl">
                  <h3 className="text-white text-sm font-semibold mb-2">Remove All Controls</h3>
                  <p className="text-gray-400 text-xs mb-6">This will remove all controls from this RCM. This action cannot be undone.</p>
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setShowClearControlsConfirm(false)} className="px-4 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors duration-200">Cancel</button>
                    <button type="button" onClick={handleRemoveAllControls} disabled={clearingControls} className="px-4 py-2 text-xs rounded-lg bg-red-600 hover:bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      {clearingControls ? 'Removing...' : 'Remove All'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {rcmControls.length === 0 ? (
              <div className="text-center py-14 text-gray-500 px-4">
                <p className="text-base sm:text-lg text-gray-300">
                  {isAutoRCM ? 'No controls synced yet' : 'No controls added yet'}
                </p>
                <p className="text-xs sm:text-sm mt-2">
                  {isAutoRCM
                    ? 'Accept mappings in Policies, then click Sync Now'
                    : 'Search and add controls from the left panel'}
                </p>
              </div>
            ) : viewMode === 'matrix' ? (
              <RiskMatrixView
                rcmControls={pagedControls}
                canEdit={canEdit}
                isDraft={rcm?.status === 'draft'}
                removeControl={removeControl}
                onOpenDrawer={setDrawerRc}
              />
            ) : (
              <div className="divide-y divide-white/5">
                <div
                  className="grid gap-2 px-4 sm:px-6 py-2 border-b border-white/5"
                  style={{ gridTemplateColumns: LIST_GRID_COLS }}
                >
                  {['ID', 'Control', 'Owner', 'Framework', 'Coverage', 'Evidence', ''].map((h, i) => (
                    <span key={i} className="text-[10px] uppercase tracking-wide text-gray-600 font-medium">{h}</span>
                  ))}
                </div>
                {pagedControls.map(rc => (
                  <div key={rc.id} className="hover:bg-white/[0.02] transition">
                    <div
                      className="grid gap-2 px-4 sm:px-6 py-3 items-center cursor-pointer"
                      style={{ gridTemplateColumns: LIST_GRID_COLS }}
                      onClick={() => setDrawerRc(rc)}
                    >
                      <span className="font-mono text-blue-400 text-xs truncate">{rc.controls?.control_id ?? '—'}</span>
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="text-white text-xs truncate">{rc.controls?.title ?? '—'}</span>
                        {rc.override && <Pencil className="w-2.5 h-2.5 text-blue-400 shrink-0" title="Customised" />}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-gray-500 truncate">
                        {rc.resolved?.process_owner
                          ? <><User className="w-3 h-3 shrink-0" />{rc.resolved.process_owner}</>
                          : <span className="text-gray-700">—</span>}
                      </span>
                      <span className="text-gray-500 text-xs truncate">
                        {rc.controls?.frameworks?.name ?? (rc.controls?.id == null ? <span className="text-gray-600 italic">gap</span> : '—')}
                      </span>
                      <CoverageStatusBadge status={rc.coverage_status} />
                      <span className="text-gray-400 text-xs">
                        {rc.evidence_count > 0
                          ? `${rc.evidence_count} item${rc.evidence_count !== 1 ? 's' : ''}`
                          : <span className="text-gray-600">None</span>}
                      </span>
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setDrawerRc(rc)}
                          className="px-2 py-1 text-[10px] rounded bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          Open
                        </button>
                        {canEdit && rcm?.status === 'draft' && (
                          <button
                            type="button"
                            onClick={() => removeControl(rc.id)}
                            className="px-2 py-1 text-[10px] bg-white/5 border border-white/10 rounded-md text-gray-500 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pager
              page={safePage}
              totalPages={totalPages}
              totalItems={totalItems}
              rangeStart={totalItems === 0 ? 0 : startIdx + 1}
              rangeEnd={endIdx}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Main RCMBuilder
// ─────────────────────────────────────────────
export default function RCMBuilder() {
  const navigate = useNavigate()

  // Page-level asset scope — same "resolve the name in sessionStorage
  // into a real UUID" pattern already used by Policies.jsx. Everything
  // this page reads/writes (RCM list, queue count, sync, detail,
  // controls, overrides, export) is scoped to this one asset.
  const selectedAppName = sessionStorage.getItem('selectedApp')
  const [pageAssetId, setPageAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [assetError, setAssetError] = useState(null)

  useEffect(() => {
      if (!selectedAppName) {
        setAssetError('No asset selected — navigate to this page via an asset.')
        setResolvingAsset(false)
        return
      }
      setResolvingAsset(true)
      API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
        .then(({ data }) => {
          setPageAssetId(data.id)
          setAssetError(null)
        })
        .catch(() => {
          setAssetError('Could not resolve the selected asset. Please re-select it from the dashboard.')
        })
        .finally(() => setResolvingAsset(false))
    }, [selectedAppName])

    const {
      createRCM, rcms, autoRCM, pendingQueueCount, failedQueueCount,
      loading: rcmsLoading, syncing, syncError, updateRCM, syncRCM, rebuildRCM,
    } = useRCMs(pageAssetId)
    const [canRebuild, setCanRebuild] = useState(false)

    useEffect(() => {
      API.get('/auth/me/')
        .then(({ data }) => setCanRebuild(['super_admin', 'client_admin'].includes(data?.role)))
        .catch(() => setCanRebuild(false))
    }, [])

    const [selectedRcmId, setSelectedRcmId] = useState(null)

    // ADDED — selectedRcmId belonged to whichever asset was active when it was
    // set; if pageAssetId changes (switching assets within the same SPA session,
    // no full reload) without this reset, stale calls fire with a mismatched
    // rcmId/asset_id pair and 404 ("No CustomRCM matches the given query").
    useEffect(() => {
      setSelectedRcmId(null)
    }, [pageAssetId])

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState(false)
  const [description, setDescription] = useState('')
  const [version, setVersion] = useState('1.0')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [assets, setAssets] = useState([])
  // Renamed from `assetId` — this is only "which asset does the NEW RCM
  // belong to" in the create-form dropdown. It's a different concept
  // from pageAssetId (which asset's RCMs are currently being viewed),
  // even though in the common single-asset-tenant case they'll match.
  const [formAssetId, setFormAssetId] = useState(null)
  const controls = useAnimation()

  useEffect(() => {
    async function fetchAssets() {
      try {
        const { data } = await API.get('/controls/assets/')
        setAssets(data || [])
        if (data?.length === 1) setFormAssetId(data[0].id)
        // Default the form to the page's own asset once resolved, when
        // there's more than one asset to choose from — saves the user a
        // redundant re-selection of the asset they're already viewing.
        else if (pageAssetId) setFormAssetId(pageAssetId)
      } catch (err) {
        console.error('Failed to fetch assets:', err?.response?.data?.detail || err.message)
      }
    }
    fetchAssets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageAssetId])

  async function triggerShake() {
    await controls.start({ x: [0, -8, 8, -6, 6, -3, 3, 0], opacity: 1, scale: 1, y: 0, transition: { duration: 0.4 } })
  }

  async function handleCreate() {
    if (!name.trim()) {
      setError('Name is required')
      setNameError(true)
      triggerShake()
      setTimeout(() => setNameError(false), 400)
      return
    }
    if (!formAssetId) {
      setError('An asset must be selected')
      triggerShake()
      return
    }
    setCreating(true)
    setError(null)
    setNameError(false)
    try {
      const rcm = await createRCM(formAssetId, name.trim(), description.trim(), version.trim() || '1.0')
      setShowCreateModal(false)
      setName(''); setDescription(''); setVersion('1.0')
      setSelectedRcmId(rcm.id)
    } catch (err) {
      setError(err.message)
    }
    setCreating(false)
  }

  function handleBack() {
    if (selectedRcmId) {
      setSelectedRcmId(null)
    } else {
      navigate('/grc/framework-review')
    }
  }

  if (resolvingAsset) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">
        <Header onBack={handleBack} onNew={() => setShowCreateModal(true)} />

        {assetError && (
          <div className="mb-6 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{assetError}</p>
          </div>
        )}

        {selectedRcmId ? (
          <motion.div key="detail" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <RCMDetail
              rcmId={selectedRcmId}
              pageAssetId={pageAssetId}
              updateRCM={updateRCM}
              syncRCM={syncRCM}
              syncing={syncing}
              syncError={syncError}
              pendingQueueCount={pendingQueueCount}
            />
          </motion.div>
        ) : (
          <motion.div key="list" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden"
          >
            <RCMList
              onSelect={setSelectedRcmId}
              onNew={() => setShowCreateModal(true)}
              rcms={rcms}
              autoRCM={autoRCM}
              pendingQueueCount={pendingQueueCount}
              failedQueueCount={failedQueueCount}
              loading={rcmsLoading}
              syncing={syncing}
              syncError={syncError}
              updateRCM={updateRCM}
              syncRCM={syncRCM}
              rebuildRCM={rebuildRCM}
              canRebuild={canRebuild}
              pageAssetId={pageAssetId}
            />
          </motion.div>
        )}
      </div>

      {showCreateModal && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
        >
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.25 }} className="w-full max-w-md sm:max-w-lg">
            <motion.div
              animate={controls}
              className={`w-full rounded-2xl bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-2xl border shadow-[0_10px_40px_rgba(0,0,0,0.6)] p-6 transition-colors duration-300 ${error ? 'border-red-500/60' : 'border-white/10'}`}
            >
              <h2 className="text-lg sm:text-xl font-semibold text-white mb-6 tracking-tight">New Risk Control Matrix</h2>
              <div className="space-y-5">
                <motion.div animate={controls}>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Name <span className="text-red-400">*</span></label>
                  <input type="text" value={name} onChange={e => { setName(e.target.value); if (nameError) { setNameError(false); setError(null) } }}
                    placeholder="e.g. Acme Corp 2025 Compliance Matrix"
                    className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 text-white placeholder-gray-500 border border-white/10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                  />
                </motion.div>
                {assets.length > 1 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Asset <span className="text-red-400">*</span></label>
                    <select
                      value={formAssetId || ''}
                      onChange={e => setFormAssetId(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 text-white border border-white/10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                    >
                      <option value="" disabled>Select an asset</option>
                      {assets.map(a => (
                        <option key={a.id} value={a.id}>{a.asset_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Description <span className="text-gray-600">(Optional)</span></label>
                  <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this RCM for?"
                    className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Version</label>
                  <input type="text" value={version} onChange={e => setVersion(e.target.value)} placeholder="1.0"
                    className="w-full px-4 py-3 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                  />
                </div>
              </div>
              {error && <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-red-400 mt-4">{error}</motion.p>}
              <div className="flex justify-end items-center gap-3 mt-8">
                <button type="button" onClick={() => { setShowCreateModal(false); setName(''); setDescription(''); setVersion('1.0'); setError(null) }}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200">
                  Cancel
                </button>
                <button type="button" onClick={handleCreate} disabled={creating}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
